import { useState } from "react";
import { Phone, Mail, MapPin, Linkedin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
  const [sending, setSending] = useState(false);

  if (!t) return null;
  const c = t.contact;
  const f = c.form;
  const socials = c.socials || {};
  const emails = c.emails || [{ value: c.email }];

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, lang });
      toast.success(f.success);
      setForm({ name: "", email: "", subject: "", message: "", company: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-creme py-20 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid gap-12 md:grid-cols-2 md:gap-16">
        {/* Info */}
        <div data-testid="contact-info">
          <span className="text-xs uppercase tracking-[0.25em] text-rouge font-medium">{c.eyebrow}</span>
          <h2 className="fluid-h2 mt-5 text-noir">{c.title}</h2>
          <p className="mt-5 text-gris leading-relaxed max-w-md">{c.text}</p>

          <div className="mt-9 space-y-5">
            <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-noir hover:text-rouge transition-colors">
              <Phone size={18} className="text-or" /> {c.phone}
            </a>
            <div className="flex items-start gap-3 text-noir">
              <Mail size={18} className="text-or mt-1 shrink-0" />
              <div className="flex flex-col">
                {emails.map((em, i) => (
                  <a key={em.value || i} href={`mailto:${em.value}`} className="hover:text-rouge transition-colors">
                    {em.value}
                  </a>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 text-noir">
              <MapPin size={18} className="text-or" /> {c.location}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-8">
            <a href={socials.linkedin || "#"} aria-label="LinkedIn" target="_blank" rel="noopener noreferrer" className="h-10 w-10 grid place-items-center rounded-full border border-ligne hover:border-rouge hover:text-rouge transition-colors">
              <Linkedin size={18} />
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="relative rounded-2xl bg-white border border-ligne overflow-hidden shadow-sm">
          <div className="h-1.5 bg-rouge" />
          <form className="p-7 md:p-9 space-y-5" onSubmit={onSubmit} data-testid="contact-form">
            {/* honeypot */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={onChange}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />
            <div>
              <label htmlFor="cf-name" className="block text-xs uppercase tracking-wider text-gris mb-2">{f.name}</label>
              <input
                id="cf-name" name="name" required value={form.name} onChange={onChange}
                data-testid="contact-form-name"
                className="w-full rounded-lg border border-ligne bg-creme/40 px-4 py-3 text-sm text-noir focus:border-rouge focus:bg-white outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="cf-email" className="block text-xs uppercase tracking-wider text-gris mb-2">{f.email}</label>
              <input
                id="cf-email" name="email" type="email" required value={form.email} onChange={onChange}
                data-testid="contact-form-email"
                className="w-full rounded-lg border border-ligne bg-creme/40 px-4 py-3 text-sm text-noir focus:border-rouge focus:bg-white outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="cf-subject" className="block text-xs uppercase tracking-wider text-gris mb-2">{f.subject}</label>
              <input
                id="cf-subject" name="subject" value={form.subject} onChange={onChange}
                data-testid="contact-form-subject"
                className="w-full rounded-lg border border-ligne bg-creme/40 px-4 py-3 text-sm text-noir focus:border-rouge focus:bg-white outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="cf-message" className="block text-xs uppercase tracking-wider text-gris mb-2">{f.message}</label>
              <textarea
                id="cf-message" name="message" required rows={5} value={form.message} onChange={onChange}
                data-testid="contact-form-message"
                className="w-full rounded-lg border border-ligne bg-creme/40 px-4 py-3 text-sm text-noir focus:border-rouge focus:bg-white outline-none transition-colors resize-none"
              />
            </div>
            <button
              type="submit" disabled={sending}
              data-testid="contact-form-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-rouge px-7 py-3.5 text-sm text-white hover:bg-[#c5291a] transition-colors disabled:opacity-60 w-full sm:w-auto"
            >
              {sending && <Loader2 size={16} className="animate-spin" />}
              {f.submit}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
